import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "auth/useAuth";
import { enqueueSnackbar } from "notistack";
import { getAPICall } from "lib/callApi";
import { CircularProgress, Stack } from "@mui/material";
import { CheckCircle, CircleOutlined } from "@mui/icons-material";
import { CHECK_TOKEN_EXPIRY } from "lib/constants";
import CoverLayout from "../components/CoverLayout";

function ChangePassword({ title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectUri = location?.search;
  const { changePassword, isLoading } = useAuth();
  const [formValues, setFormValues] = useState({
    password: {
      value: "",
      error: false,
      errorMessage: "",
    },
    confirmPassword: {
      value: "",
      error: false,
      errorMessage: "",
    },
  });

  const checkTokenExpireOrNot = async () => {
    const token = location.pathname.split("/")[2];

    const result = await getAPICall(CHECK_TOKEN_EXPIRY + token, false);

    if (result.data.success === false) {
      if (result.data.invalidUrl) {
        navigate("/invalid-invitation");
        return;
      }
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: result.data.message || "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const changeHandler = async (event, name) => {
    const { value } = event.target;

    if (name === "password") {
      const isLengthValid = value.length >= 8;
      const hasLowerCase = /[a-z]/.test(value);
      const hasUpperCase = /[A-Z]/.test(value);
      const hasDigit = /[\d]/.test(value);
      const hasSymbolWhitespace = /[$*.[\]{}()?\-"!@#%&,><':;|_~`+=/\\^]/.test(value);

      const isValid =
        isLengthValid && hasLowerCase && hasUpperCase && hasDigit && hasSymbolWhitespace;

      setFormValues({
        ...formValues,
        [name]: {
          ...formValues[name],
          value,
          error: !isValid,
          errorMessage: "The password must fulfill all of the criteria",
          isLengthValid,
          hasLowerCase,
          hasUpperCase,
          hasDigit,
          hasSymbolWhitespace,
        },
      });
    } else if (name === "confirmPassword") {
      setFormValues({
        ...formValues,
        [name]: {
          ...formValues[name],
          value,
          error: formValues.password.value !== value,
          errorMessage:
            formValues.password.value !== value
              ? "Password and confirm password are mismatched"
              : "",
        },
      });
    }
  };

  const onSubmit = async (e) => {
    if (e) {
      e?.preventDefault?.();
    }

    let newFormValues = { ...formValues };
    const formFields = Object.keys(formValues);
    const token = location.pathname.split("/")[2];

    for (let index = 0; index < formFields.length; index += 1) {
      const currentField = formFields[index];
      const currentValue = formValues[currentField].value;

      if (currentValue === "") {
        newFormValues = {
          ...newFormValues,
          [currentField]: {
            ...newFormValues[currentField],
            error: true,
            errorMessage: "Field is required",
          },
        };
      } else if (currentField === "confirmPassword") {
        if (
          currentValue.length !== formValues.password.value.length ||
          currentValue !== formValues.password.value
        ) {
          newFormValues = {
            ...newFormValues,
            [currentField]: {
              ...newFormValues[currentField],
              error: true,
              errorMessage: "Password and confirm password must match",
            },
          };
        }
      }
    }

    setFormValues({ ...newFormValues });
    const foundErrorValue = Object.values(newFormValues).find((value) => value.error === true);
    if (!foundErrorValue) {
      const dataToPass = {
        password: newFormValues.password.value,
        token,
      };

      changePassword(dataToPass, redirectUri);
    }
  };

  useEffect(() => {
    checkTokenExpireOrNot();
  }, []);

  return (
    <CoverLayout coverHeight="40vh">
      {isLoading ? (
        <Stack alignItems="center">
          <CircularProgress
            sx={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9,
            }}
            color="info"
            size="10vh"
          />
        </Stack>
      ) : (
        ""
      )}
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="secondary"
          mx={2}
          mt={-3}
          py={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h3" fontWeight="medium" color="white" mt={1}>
            {title}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox
            component="form"
            role="form"
            sx={{
              "& .MuiFormHelperText-root": { color: "#d32f2f" },
            }}
            onSubmit={onSubmit}
          >
            <MDBox mb={4}>
              <MDInput
                type="password"
                label="New Password*"
                variant="standard"
                fullWidth
                error={formValues.password.error}
                helperText={formValues.password.errorMessage}
                onChange={(e) => changeHandler(e, "password")}
                name="password"
              />
            </MDBox>
            <MDBox mb={4}>
              <MDInput
                type="password"
                label="Confirm Password*"
                variant="standard"
                fullWidth
                error={formValues.confirmPassword.error}
                helperText={formValues.confirmPassword.errorMessage}
                onChange={(e) => changeHandler(e, "confirmPassword")}
                name="confirmPassword"
              />
            </MDBox>
            <MDBox mb={4}>
              <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start">
                {formValues.password.isLengthValid ? <CheckCircle /> : <CircleOutlined />}
                <MDTypography variant="body2" sx={{ mx: 1 }}>
                  At least 8 characters
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start">
                {formValues.password.hasLowerCase ? <CheckCircle /> : <CircleOutlined />}
                <MDTypography variant="body2" sx={{ mx: 1 }}>
                  One lowercase character
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start">
                {formValues.password.hasUpperCase ? <CheckCircle /> : <CircleOutlined />}
                <MDTypography variant="body2" sx={{ mx: 1 }}>
                  One uppercase character
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start">
                {formValues.password.hasDigit ? <CheckCircle /> : <CircleOutlined />}
                <MDTypography variant="body2" sx={{ mx: 1 }}>
                  One number
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start">
                {formValues.password.hasSymbolWhitespace ? <CheckCircle /> : <CircleOutlined />}
                <MDTypography variant="body2" sx={{ mx: 1 }}>
                  One symbol
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox mt={6} mb={1}>
              <MDButton color="primaryRed" fullWidth onClick={onSubmit} type="submit">
                submit
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default ChangePassword;
